import { ensureError } from '@hover/utilities';
import { useLocation, useParams } from 'react-router';
import type { AnyObject, InferType, ObjectSchema } from 'yup';
import { ValidationError } from 'yup';

export class ParseParamsError extends Error {
  failure: ValidationError | null = null;

  constructor(error: Error) {
    super(error.message);

    // I believe this is only necessary when we're transpiling to ES5. It should
    // be possible to sub-class built-ins with native ES6 class syntax, but this
    // is required for ES5 (otherwise `instanceof` won't work as expected).
    Object.setPrototypeOf(this, ParseParamsError.prototype);

    if (error instanceof ValidationError) {
      this.failure = error;
    }

    this.name = 'ParseParamsError';
  }
}

type ParamParseFailure = Record<string, undefined> & {
  __error: Error;
};

export const parseParams = <
  TSchema extends ObjectSchema<{}, AnyObject>,
  TStrict extends boolean = false,
>(
  params: Record<string, unknown>,
  schema: TSchema,
  strict?: TStrict,
): TStrict extends true
  ? InferType<TSchema>
  : InferType<TSchema> | ParamParseFailure => {
  try {
    return schema.validateSync(params);
  } catch (caught) {
    const error = new ParseParamsError(ensureError(caught));

    if (strict) throw error;

    return { __error: error };
  }
};

/**
 * Parses URL parameters based on a given schema.
 *
 * @template TSchema - The schema type used for validation.
 * @template TStrict - A boolean indicating whether strict parsing is enforced.
 *
 * @param {TSchema} schema - The schema to validate the URL parameters against.
 * @param {TStrict} [strict] - Optional. If true, enforces strict parsing.
 *
 * @returns {TStrict extends true ? InferType<TSchema> : InferType<TSchema> | ParamParseFailure}
 * Returns the parsed parameters if validation is successful. If `strict` is true,
 * returns the inferred type of the schema. Otherwise, returns either the inferred
 * type of the schema or a `ParamParseFailure` object.
 */
export const useParsedParams = <
  TSchema extends ObjectSchema<{}, AnyObject>,
  TStrict extends boolean = false,
>(
  schema: TSchema,
  strict?: TStrict,
): TStrict extends true
  ? InferType<TSchema>
  : InferType<TSchema> | ParamParseFailure => {
  const params = useParams();

  return parseParams(params, schema, strict);
};

/**
 * Parses URL query parameters based on a given schema.
 *
 * @template TSchema - The schema type used for validation.
 * @template TStrict - A boolean indicating whether strict parsing is enforced.
 *
 * @param {TSchema} schema - The schema to validate the URL parameters against.
 * @param {TStrict} [strict] - Optional. If true, enforces strict parsing.
 *
 * @returns {TStrict extends true ? InferType<TSchema> : InferType<TSchema> | ParamParseFailure}
 * Returns the parsed parameters if validation is successful. If `strict` is true,
 * returns the inferred type of the schema. Otherwise, returns either the inferred
 * type of the schema or a `ParamParseFailure` object.
 */
export const useParsedQueryParams = <
  TSchema extends ObjectSchema<{}, AnyObject>,
  TStrict extends boolean = false,
>(
  schema: TSchema,
  strict?: TStrict,
): TStrict extends true
  ? InferType<TSchema>
  : InferType<TSchema> | ParamParseFailure => {
  const { search } = useLocation();

  const params = Array.from(
    new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    ).entries(),
  ).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value,
    }),
    {} as Record<string, string>,
  );

  return parseParams(params, schema, strict);
};
